import * as React from "react"
import { keyframes } from "@emotion/react"
import { Box, Skeleton, Typography, useTheme } from "@mui/material"
import { TableSeeker } from "@ui-seeker/core"
import { IconSeeker } from "@ui-seeker/core"

import { availabilityColumn, brandColumn, colorColumn, companyColumn, customersColumn, displayNameColumn, optionsColumn, switchColumn } from "./columns"
import AdminBrandsTableToolbar from "./toolBar"
import { useAdminBrandSearch, useMerge } from "../@hooks"
import { USER_ENV } from "@constants"
import { useUserApp } from "@hooks"
import { isLoadingAdminBrands, tableData, selectedBrandIds } from "../@signals"


function EmptyState() {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                color: "text.secondary"
            }}
        >
            <IconSeeker icon="box-open" sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
                No brands found
            </Typography>
            <Typography variant="body2" color="text.disabled">
                Create a new brand using the + button above
            </Typography>
        </Box>
    )
}


export default function AdminBrandsTable() {
    const { palette } = useTheme()
    useAdminBrandSearch()
    const { handleRowSelectionChange } = useMerge()
    // Customer availability is a Puig-only feature here: retail's `/context`
    // ships no `customerAvailability`, so the columns would have nothing to
    // read. The session's `env` comes from the backend (Config.ENV).
    const { userEnv } = useUserApp()
    const isPuig = userEnv === USER_ENV.PUIG

    if (isLoadingAdminBrands.value)
        return <Skeleton variant="rectangular" height={500} sx={{ mt: 2, borderRadius: 1 }} />

    if (!tableData.value.length)
        return <EmptyState />

    return <TableSeeker
        data={tableData.value}
        title={`Brands (${tableData.value.length})`}
        columns={[
            brandColumn(),
            displayNameColumn(),
            companyColumn(),
            customersColumn(),
            ...(isPuig ? [
                availabilityColumn("siteNavigations", "Site Navigations"),
                availabilityColumn("banners", "Banners")
            ] : []),
            colorColumn(),
            ...switchColumn("validated", "Validated"),
            optionsColumn()
        ]}
        options={{
            selectableRows: "multiple",
            selectableRowsHeader: true,
            selectableRowsHideCheckboxes: false,
            selectToolbarPlacement: "none",
            rowsPerPage: 25,
            rowsPerPageOptions: [10, 25, 50, 100],
            rowsSelected: tableData.value
                .map((brand, index) => selectedBrandIds.value.includes(brand.brandID) ? index : -1)
                .filter(index => index !== -1),
            onRowSelectionChange: handleRowSelectionChange,
            enhancedSetRowProps: () => ({
                sx: {
                    "&:has(.is-loading)": {
                        animation: `${keyframes({
                            "0%": { backgroundColor: palette.info.light },
                            "100%": { backgroundColor: palette.info.lighter }
                        })} 1s linear infinite alternate`
                    },
                    "&:has(.is-open)": {
                        backgroundColor: "info.lighter",
                        "&:hover": { backgroundColor: "info.light" }
                    }
                }
            }),
            customToolbar: () => <AdminBrandsTableToolbar />
        }}
    />
}
